import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Modal } from '@/components/ui/modal'

export interface MetricFormData {
  name: string
  description: string
  unit: string
  calculationMode: 'incremental' | 'overwrite'
  visibleToClient: boolean
}

export interface CreateMetricModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: MetricFormData) => void | Promise<void>
  isLoading?: boolean
}

export function CreateMetricModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: CreateMetricModalProps) {
  const [formData, setFormData] = useState<MetricFormData>({
    name: '',
    description: '',
    unit: '',
    calculationMode: 'incremental',
    visibleToClient: true,
  })

  const handleInputChange = (field: keyof MetricFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.unit.trim()) {
      return
    }

    await onSubmit(formData)
    setFormData({
      name: '',
      description: '',
      unit: '',
      calculationMode: 'incremental',
      visibleToClient: true,
    })
  }

  const handleCancel = () => {
    onOpenChange(false)
    setFormData({
      name: '',
      description: '',
      unit: '',
      calculationMode: 'incremental',
      visibleToClient: true,
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Créer la métrique
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Ajouter une métrique</h2>
            <p className="text-sm text-gray-500 mt-1">
              Créez une nouvelle métrique pour suivre les performances du projet
            </p>
          </div>
        </div>

        <FormField label="Nom de la métrique" required>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Ex: Messages traités"
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        <FormField label="Description" required>
          <Textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Description de la métrique visible par le client"
            rows={3}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
          />
        </FormField>

        <FormField label="Unité" required>
          <Input
            type="text"
            value={formData.unit}
            onChange={(e) => handleInputChange('unit', e.target.value)}
            placeholder="Ex: messages, €, %, secondes"
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Mode de calcul</label>
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={() => handleInputChange('calculationMode', 'incremental')}
              variant="outline"
              className={
                formData.calculationMode === 'incremental'
                  ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-md border-transparent hover:from-cyan-500 hover:to-cyan-600'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
              }
            >
              Incrémental
            </Button>
            <Button
              type="button"
              onClick={() => handleInputChange('calculationMode', 'overwrite')}
              variant="outline"
              className={
                formData.calculationMode === 'overwrite'
                  ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-md border-transparent hover:from-cyan-500 hover:to-cyan-600'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'
              }
            >
              Écrasement
            </Button>
          </div>
          {formData.calculationMode === 'incremental' && (
            <p className="text-sm text-gray-500 mt-2">
              Les nouvelles valeurs s'ajoutent aux précédentes
            </p>
          )}
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">
              Visible par le client
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Le client pourra voir cette métrique dans son dashboard
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleInputChange('visibleToClient', !formData.visibleToClient)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.visibleToClient ? 'bg-cyan-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.visibleToClient ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </Modal>
  )
}

